import { RectangleFViewModel } from "../../common/rectangle-f/rectangle-f.view-model";
import {
  CropSettings,
  MediaInfo,
  MediaItem,
  MediaSegment,
} from "../../../domain/media";
import { ICommonUtilInterface } from "../../../domain/common/common-util.interface";
import { RectangleF } from "../../../domain/rectangle-f";
import { LiveFeedInfo } from "../../../domain/live-feed";
import { GraphicsInfo } from "../../../domain/graphics-info";
import {
  ELayoutZoneType,
  LayoutZone,
} from "../../../domain/layouts/layoutZone";
import { Layout } from "../../../domain/layouts/layout";
import { ILayoutsRepository } from "../../../domain/layouts/layouts.repository";
import { ToastrService } from "ngx-toastr";
import { Router } from "@angular/router";
import { ChannelRepository } from "../../../domain/channel-repository";
import moment from "moment";
import { NgxSmartModalService } from "ngx-smart-modal";
import { MediaRepository } from "../../../domain/media-repository";
import { ChangeDetectorRef } from "@angular/core";

export class LayoutViewModel extends Layout {
  public windowHeightPx = window.innerHeight * 0.9 + "px";
  private _viewPortHeight = 50;
  public get viewPortWidth(): number {
    return this.viewPortHeight * this.aspectRatio;
  }

  public get viewPortHeight(): number {
    return this._viewPortHeight;
  }

  constructor(
    public id: string,
    public name: string,
    public zones: LayoutZoneViewModel[],
    public aspectRatio: number,
    private _commonUtils: ICommonUtilInterface,
    private _layoutsRepository: ILayoutsRepository,
    private _toastr: ToastrService,
    private _channelRepository: ChannelRepository,
    private _mediaRepository: MediaRepository,
    private _ngxSmartModalService: NgxSmartModalService,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {
    super(id, name, zones);
    if (zones.length === 0) {
      throw new Error("zones must have at least 1 zone as a base zone.");
    }
    if (zones[0].zoneType === ELayoutZoneType.gfx) {
      throw new Error("gfx cannot be used as a base zone.");
    }
  }
  public onInit(): void {
    const baseZoneVM = this.baseZone as LayoutZoneViewModel;
    baseZoneVM.positionVM.updated.subscribe((p) => {
      this.setMeasureType(baseZoneVM.positionVM.selectedMeasureType);
    });
  }

  onWindowResize(event: UIEvent) {
    this.windowHeightPx = window.innerHeight * 0.9 + "px";
  }

  private _syncRectangleFWithViewModel() {
    for (let i = 0; i < this.zones.length; i++) {
      this.zones[i].position = this.zones[i].positionVM.toRectangleF();
    }
  }

  async applyClicked() {
    console.log("layouts.view-model->applyClicked", this);
    try {
      if (this.baseZone.zoneType === ELayoutZoneType.empty) {
        this._toastr.error(
          `Error while saving the layout\nThe base zone can't be empty`,
        );
      } else {
        this._syncRectangleFWithViewModel();
        await this._layoutsRepository.createOrUpdate(this);
        await this._channelRepository.updatePlaylistBy(null, [this.id]);
        this._toastr.success(`Layout has been saved!`);
      }
    } catch (e) {
      this._toastr.error(`Error while saving the layout\n${e}`);
    }
  }

  async onDrop(event: DragEvent, zone: LayoutZoneViewModel) {
    console.log(event);
    console.log(
      "layout-view-moder->onDrop->types:",
      event.dataTransfer.types.join(","),
    );
    if (
      event.dataTransfer &&
      event.dataTransfer.types &&
      event.dataTransfer.types.length > 0
    ) {
      for (let i = 0; i < event.dataTransfer.types.length; i++) {
        const mediaType = event.dataTransfer.types[i];
        switch (mediaType) {
          case "text/media":
            const draggedMedia: MediaItem = JSON.parse(
              event.dataTransfer.getData("text/media"),
            );
            const mediaInfo = await this._mediaRepository.getMediaInfo(
              draggedMedia.id,
            );
            if (mediaInfo && mediaInfo.segments.length > 0) {
              await this._mediaItemWithSegmentsDraggedEventHandler(
                mediaInfo,
                zone,
              );
              break;
            }
            zone.zoneType = ELayoutZoneType.media;
            zone.mediaId = draggedMedia.id;
            zone.offsetFromBaseZoneMs = 0;
            zone.somMs = 0;
            zone.keepShowDuration = true;
            zone.mediaName = draggedMedia.name;
            zone.mediaThumbnailUrl = draggedMedia.contentSnapshotUrl;
            break;
          case "text/feed":
            const draggedLiveFeed: LiveFeedInfo = JSON.parse(
              event.dataTransfer.getData("text/feed"),
            );
            zone.zoneType = ELayoutZoneType.liveFeed;
            zone.mediaId = draggedLiveFeed.id;
            zone.offsetFromBaseZoneMs = 0;
            zone.somMs = 0;
            zone.keepShowDuration = true;
            zone.mediaName = draggedLiveFeed.name;
            zone.mediaThumbnailUrl = draggedLiveFeed.thumbnail;
            break;

          case "text/graphics":
            let graphicsInfo: GraphicsInfo = JSON.parse(
              event.dataTransfer.getData("text/graphics"),
            );
            if (graphicsInfo.isTemplate === false) {
              if (zone.isBaseZone) {
                this._toastr.warning("Gfx can't be used as a base zone!");
              } else {
                zone.zoneType = ELayoutZoneType.gfx;
                zone.mediaId = graphicsInfo.id;
                zone.mediaName = graphicsInfo.name;
                zone.offsetFromBaseZoneMs = 0;
                zone.keepShowDuration = true;
                zone.somMs = 0;
                zone.mediaThumbnailUrl = graphicsInfo.thumbnail;
              }
            }
            break;
        }
      }
    }

    this._changeDetectorRef.markForCheck();
    this._changeDetectorRef.detectChanges();
  }

  addNewZoneClicked() {
    const newZoneVM = new LayoutZoneViewModel(
      Math.max(...this.zones.map((z) => z.index)) + 1,
      77,
      100,
      RectangleFViewModel.fromRectangleFObject(
        new RectangleF(0.2, 0.3, 0.3, 0.3),
        false,
      ),
      ELayoutZoneType.empty,
      null,
      "Empty - No Media Selected",
      null,
      CropSettings.createNoCropSettings(false),
      0,
      0,
    );
    if (this.zones[0].lastDimensionType > 0) {
      newZoneVM.positionVM.setMeasureType(this.zones[0].lastDimensionType);
    }
    this.zones.push(newZoneVM);
  }

  setMeasureType(measureType: number) {
    for (let i = 1; i < this.zones.length; i++) {
      const zone = this.zones[i] as LayoutZoneViewModel;
      zone.positionVM.setMeasureType(measureType);
    }
  }

  deleteZone(zoneToRemove: LayoutZoneViewModel) {
    this.zones.splice(this.zones.indexOf(zoneToRemove), 1);
  }

  private async _mediaItemWithSegmentsDraggedEventHandler(
    media: MediaInfo,
    zone: LayoutZoneViewModel,
  ) {
    this._ngxSmartModalService.setModalData(
      {
        mediaId: media.id,
        onSegmentsSelectedCallback: (segments: MediaSegment[]) => {
          if (segments && segments[0]) {
            const chosenSegment = segments[0];
            zone.zoneType = ELayoutZoneType.media;
            zone.mediaId = media.id;
            zone.keepShowDuration = false;
            zone.durationMs = chosenSegment.duration;
            zone.offsetFromBaseZoneMs = 0;
            zone.somMs = chosenSegment.timeInMs;
            zone.mediaName = media.name;
            zone.mediaThumbnailUrl = media.contentSnapshotUrl;
          }
        },
        currentChannel: null,
        allowMultiSelect: false,
        onDataBindEnded: () => {
          this._changeDetectorRef.markForCheck();
          this._changeDetectorRef.detectChanges();
        },
      },
      "chooseSegmentModal",
      true,
    );
    this._ngxSmartModalService.getModal("chooseSegmentModal").open();
  }

  public static createNewLayout(
    aspectRatio: number,
    commonUtils: ICommonUtilInterface,
    layoutsRepository: ILayoutsRepository,
    toastr: ToastrService,
    channelRepository: ChannelRepository,
    mediaRepository: MediaRepository,
    ngxSmartModalService: NgxSmartModalService,
    changeDetectorRef: ChangeDetectorRef,
  ): LayoutViewModel {
    const layoutZones = [];
    /*layoutZones.push(new LayoutZoneViewModel('0', 0, 22, 73, RectangleFViewModel.fromRectangleFObject(RectangleF.DEFAULT), ELayoutZoneType.media, 1000513353, '_NA_PromoSet3NEW_July22.mp4','https://s3.eu-central-1.wasabisys.com/icpublicuploads/c95cf67c-1d51-ec11-90f1-a0423f495001/thumb/thumb_d3d2ab06-9b02-ed11-90f1-a0423f495001.png'));
    layoutZones.push(new LayoutZoneViewModel('1', 1, 32, 95, RectangleFViewModel.fromRectangleFObject(new RectangleF(0.5, 0.5, 0.5,0.5)), ELayoutZoneType.media, 1000330170, '6 Month Madness','https://s3.eu-central-1.wasabisys.com/icpublicuploads/c95cf67c-1d51-ec11-90f1-a0423f495001/thumb/thumb_e8337e75-5d63-ec11-90f1-a0423f495001.png'));*/

    layoutZones.push(
      new LayoutZoneViewModel(
        0,
        100,
        0,
        RectangleFViewModel.fromRectangleFObject(RectangleF.DEFAULT),
        ELayoutZoneType.empty,
        null,
        "Empty - No Media Selected",
        null,
        CropSettings.createNoCropSettings(false),
        0,
        0,
      ),
    );
    return new LayoutViewModel(
      commonUtils.generateGuid(),
      "New Layout",
      layoutZones,
      aspectRatio,
      commonUtils,
      layoutsRepository,
      toastr,
      channelRepository,
      mediaRepository,
      ngxSmartModalService,
      changeDetectorRef,
    );
  }
}
export class LayoutZoneViewModel extends LayoutZone {
  public get keepShowDuration(): boolean {
    return isNaN(this.durationMs) || this.durationMs === null;
  }
  public set keepShowDuration(newVal: boolean) {
    if (newVal) {
      this.durationMs = null;
    } else {
      this.durationMs = 0;
    }
  }
  constructor(
    public index: number,
    public volume: number,
    public zLayer: number,
    public positionVM: RectangleFViewModel,
    public zoneType: ELayoutZoneType,
    public mediaId: any,
    public mediaName: string,
    public mediaThumbnailUrl: string,
    public cropSettings: CropSettings,
    public offsetFromBaseZoneMs: number,
    public somMs: number,
    public durationMs?: number,
  ) {
    super(
      index,
      volume,
      zLayer,
      positionVM.toRectangleF(),
      zoneType,
      mediaId,
      mediaName,
      mediaThumbnailUrl,
      cropSettings,
      offsetFromBaseZoneMs,
      somMs,
      durationMs,
    );
    this.lastDimensionType = positionVM.selectedMeasureType;
  }

  public lastDimensionType: number;

  public get isBaseZone(): boolean {
    return this.index === 0;
  }

  public editMediaClicked(router: Router) {
    switch (this.zoneType) {
      case ELayoutZoneType.liveFeed:
        router
          .navigate(["/scheduler/live/edit/", this.mediaId], {
            queryParamsHandling: "preserve",
          })
          .then();
        break;
      case ELayoutZoneType.gfx:
        router
          .navigate(["/scheduler/graphics/edit/", this.mediaId], {
            queryParamsHandling: "preserve",
          })
          .then();
        break;
      case ELayoutZoneType.media:
        router
          .navigate(["/mam/editMedia", this.mediaId], {
            queryParamsHandling: "preserve",
          })
          .then();
        break;
    }
  }

  public get mediaTypeAsString(): string {
    switch (this.zoneType) {
      case ELayoutZoneType.liveFeed:
        return "LIVE FEED";
      case ELayoutZoneType.gfx:
        return "GFX";
      case ELayoutZoneType.media:
        return "MEDIA";
      default:
        return "";
    }
  }

  public get styleObject(): any {
    const position = this.positionVM.toRectangleF();
    return {
      top: position.y * 100 + "%",
      left: position.x * 100 + "%",
      position: "absolute",
      width: position.width * 100 + "%",
      height: position.height * 100 + "%",
      /*'background-color': 'green',*/
      /*'background-image': 'url("' + this.mediaThumbnailUrl + 'hh")',*/
      "background-repeat": "no-repeat",
      "background-color": "#F1F3F4",
      "background-size": "cover",
      "z-index": this.isBaseZone ? this.zLayer : 100 - this.zLayer,
    };
  }

  dimensionTypeChanged(event, layoutViewModel: LayoutViewModel) {
    const type = parseInt(event.target.value);
    layoutViewModel.setMeasureType(type);
    this.lastDimensionType = type;
  }
}
